import React, { useState, forwardRef } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { Row, Col, Form, FloatingLabel, Spinner } from "react-bootstrap";
import { SSMemberServices } from "../../services/SSMemberService";
import AlertModal from "../../components/AlertModal";
import { validateEmail } from "../../utils/validations";
import styles from "../../styles/Modal.module.css";

const AddSSMembers = (props) => {
  const [formData, setFormData] = useState({
    name: '',
    mobileNo: '',
    email: '',
    designation: '',
    referalHospital: ''
  });
  const [loading, setLoading] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertData, setAlertData] = useState({ title: '', message: '', variant: 'info' });

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email if provided
    if (formData.email && !validateEmail(formData.email)) {
      setAlertData({
        title: 'Invalid Email',
        message: 'Please enter a valid email address (e.g., example@domain.com)',
        variant: 'warning'
      });
      setShowAlertModal(true);
      return;
    }
    
    setLoading(true);

    try {
      const response = await SSMemberServices.addSSMember({
        name: formData.name.trim(),
        mobileNo: formData.mobileNo.trim(),
        email: formData.email ? formData.email.trim() : null,
        designation: formData.designation.trim(),
        referalHospital: formData.referalHospital || null
      });

      if (response && (response.status === 200 || response.status === 201)) {
        // Close the add modal first
        if (props.onHide) {
          props.onHide();
        }
        
        // Reset form
        setFormData({
          name: '',
          mobileNo: '',
          email: '',
          designation: '',
          referalHospital: ''
        });
        
        // Show success alert
        setAlertData({
          title: 'Success',
          message: 'Member added successfully!',
          variant: 'success'
        });
        setShowAlertModal(true);
        
        // Refresh the members list
        if (props.onClose) {
          props.onClose();
        }
      } else {
        throw new Error('Failed to add member');
      }
    } catch (error) {
      console.error('Error adding member:', error);
      setAlertData({
        title: 'Error',
        message: error.message || 'Failed to add member. Please try again.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAlertData({ title: '', message: '', variant: 'info' });
    props.onHide();
  };
  return (
    <>
      <Modal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className={styles.modalShow}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title id="contained-modal-title-vcenter" className={styles.modalTitle}>
            Add Secondary Screening Member
          </Modal.Title>
        </Modal.Header>
        <Form id="addSSMemberForm" onSubmit={handleSubmit}>
          <Modal.Body className={styles.modalBody}>
            {loading ? (
              <div className="text-center p-3">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Adding member...</span>
                </Spinner>
                <p className="mt-2">Adding new member...</p>
              </div>
            ) : (
              <>
                <Row>
                  <Col>
                    <FloatingLabel controlId="name" label="Name*" className="mb-3">
                      <Form.Control 
                        type="text" 
                        placeholder="Name*" 
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </FloatingLabel>
                  </Col>
                  <Col>
                    <FloatingLabel controlId="mobileNo" label="Mobile No*" className="mb-3">
                      <Form.Control 
                        type="tel" 
                        placeholder="Mobile No*" 
                        value={formData.mobileNo}
                        onChange={handleChange}
                        required
                        pattern="[0-9]{10}"
                        title="Please enter a valid 10-digit mobile number"
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FloatingLabel controlId="email" label="Email" className="mb-3">
                      <Form.Control 
                        type="email" 
                        placeholder="example@domain.com" 
                        value={formData.email}
                        onChange={handleChange}
                        pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                        title="Please enter a valid email address (e.g., example@domain.com)"
                      />
                    </FloatingLabel>
                  </Col>
                  <Col>
                    <FloatingLabel controlId="designation" label="Designation*" className="mb-3">
                      <Form.Control 
                        type="text" 
                        placeholder="Designation*" 
                        value={formData.designation}
                        onChange={handleChange}
                        required
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FloatingLabel controlId="referalHospital" label="Hospital" className="mb-3">
                      <Form.Control 
                        type="text" 
                        placeholder="Hospital" 
                        value={formData.referalHospital}
                        onChange={handleChange}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className={styles.modalFooter}>
            <Button 
              variant="success" 
              type="submit"
              form="addSSMemberForm"
              disabled={loading}
              className={styles.primaryButton}
            >
              {loading ? 'Adding...' : 'Add New Member'}
            </Button>
            <Button 
              variant="danger" 
              onClick={props.onHide}
              className={styles.secondaryButton}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={alertData.title}
        message={alertData.message}
        variant={alertData.variant}
      />
    </>
  );
};

export default AddSSMembers;
